// Questionnaire types - comprehensive financial advisor interview

// ============================================================================
// QUESTION TYPES
// ============================================================================

export type QuestionType = 
  | 'text'
  | 'number'
  | 'currency'
  | 'percentage'
  | 'date'
  | 'select'
  | 'multi_select'
  | 'radio'
  | 'checkbox'
  | 'slider'
  | 'year'
  | 'state'
  | 'industry'

export type QuestionSection = 
  | 'personal'
  | 'family'
  | 'career'
  | 'finances'
  | 'insurance'
  | 'goals'
  | 'preferences'

// ============================================================================
// QUESTION DEFINITION
// ============================================================================

export interface QuestionOption {
  value: string
  label: string
  description?: string
  icon?: string
}

export interface QuestionValidation {
  required?: boolean
  min?: number
  max?: number
  pattern?: string
  customValidator?: string       // Function name for complex validation
}

export interface QuestionCondition {
  questionId: string
  operator: 'equals' | 'not_equals' | 'greater_than' | 'less_than' | 'includes'
  value: string | number | boolean
}

export interface Question {
  id: string
  section: QuestionSection
  subsection?: string
  order: number
  
  // Display
  question: string
  description?: string
  helpText?: string
  placeholder?: string
  icon?: string
  
  // Input type
  type: QuestionType
  options?: QuestionOption[]
  
  // Validation
  validation?: QuestionValidation
  
  // Conditional display
  showIf?: QuestionCondition[]   // Show only if ALL conditions met
  hideIf?: QuestionCondition[]   // Hide if ANY condition met
  
  // Data binding
  stateKey: string               // Path in LifePlanState (e.g., "personal.birthDate")
  
  // Behavior
  allowSkip?: boolean
  defaultValue?: string | number | boolean
  
  // For repeatable questions (children, debts, etc.)
  isRepeatable?: boolean
  repeatableKey?: string         // Key for the array (e.g., "family.children")
  repeatableItemLabel?: string   // e.g., "Child"
}

// ============================================================================
// SECTION DEFINITION
// ============================================================================

export interface QuestionnaireSection {
  id: QuestionSection
  title: string
  description: string
  icon: string
  estimatedMinutes: number
  questions: Question[]
}

// ============================================================================
// QUESTIONNAIRE STATE
// ============================================================================

export interface QuestionnaireProgress {
  currentSection: QuestionSection
  currentQuestionIndex: number
  completedSections: QuestionSection[]
  skippedQuestions: string[]
  answers: Record<string, unknown>  // questionId -> answer
  startedAt: string
  lastUpdated: string
}

export interface QuestionnaireState {
  isOpen: boolean
  isCompleted: boolean
  progress: QuestionnaireProgress
  sections: QuestionnaireSection[]
}

// ============================================================================
// INITIAL STATE
// ============================================================================

export const createInitialQuestionnaireState = (): QuestionnaireState => ({
  isOpen: false,
  isCompleted: false,
  progress: {
    currentSection: 'personal',
    currentQuestionIndex: 0,
    completedSections: [],
    skippedQuestions: [],
    answers: {},
    startedAt: '',
    lastUpdated: '',
  },
  sections: [],  // Will be populated with question definitions
})

// ============================================================================
// SECTION METADATA
// ============================================================================

export const SECTION_INFO: Record<QuestionSection, { title: string; description: string; icon: string; estimatedMinutes: number }> = {
  personal: {
    title: 'Personal Information',
    description: 'Tell us about yourself - your age, location, and retirement goals.',
    icon: 'User',
    estimatedMinutes: 2,
  },
  family: {
    title: 'Family Planning',
    description: 'Your family situation - spouse, children, and future plans.',
    icon: 'Users',
    estimatedMinutes: 3,
  },
  career: {
    title: 'Career & Income',
    description: 'Your job, industry, salary, and career trajectory.',
    icon: 'Briefcase',
    estimatedMinutes: 4,
  },
  finances: {
    title: 'Current Finances',
    description: 'Your assets, debts, and monthly cash flow.',
    icon: 'Wallet',
    estimatedMinutes: 5,
  },
  insurance: {
    title: 'Insurance & Protection',
    description: 'Health, life, disability, and other insurance coverage.',
    icon: 'Shield',
    estimatedMinutes: 3,
  },
  goals: {
    title: 'Goals & Dreams',
    description: 'What you want to achieve - big purchases, legacy, charitable giving.',
    icon: 'Target',
    estimatedMinutes: 3,
  },
  preferences: {
    title: 'Preferences & Risk',
    description: 'Your risk tolerance, Social Security plans, and other preferences.',
    icon: 'Settings',
    estimatedMinutes: 2,
  },
}

export const SECTION_ORDER: QuestionSection[] = [
  'personal',
  'family',
  'career',
  'finances',
  'insurance',
  'goals',
  'preferences',
]

// ============================================================================
// VALIDATION HELPERS
// ============================================================================

export interface ValidationResult {
  isValid: boolean
  errors: string[]
}

export function validateAnswer(question: Question, value: unknown): ValidationResult {
  const errors: string[] = []
  const validation = question.validation
  
  if (!validation) {
    return { isValid: true, errors: [] }
  }
  
  // Required check
  if (validation.required && (value === undefined || value === null || value === '')) {
    errors.push('This field is required')
  }
  
  // Number range checks
  if (typeof value === 'number') {
    if (validation.min !== undefined && value < validation.min) {
      errors.push(`Must be at least ${validation.min}`)
    }
    if (validation.max !== undefined && value > validation.max) {
      errors.push(`Must be at most ${validation.max}`)
    }
  }
  
  // Pattern check
  if (validation.pattern && typeof value === 'string') {
    const regex = new RegExp(validation.pattern)
    if (!regex.test(value)) {
      errors.push('Invalid format')
    }
  }
  
  return { isValid: errors.length === 0, errors }
}

// ============================================================================
// PROGRESS CALCULATION
// ============================================================================

export function calculateProgress(
  sections: QuestionnaireSection[],
  answers: Record<string, unknown>,
  skippedQuestions: string[]
): number {
  let totalQuestions = 0
  let answeredQuestions = 0
  
  sections.forEach(section => {
    section.questions.forEach(question => {
      // Only count visible questions (not ones hidden by conditions)
      totalQuestions++
      if (answers[question.id] !== undefined || skippedQuestions.includes(question.id)) {
        answeredQuestions++
      }
    })
  })
  
  return totalQuestions > 0 ? Math.round((answeredQuestions / totalQuestions) * 100) : 0
}
